import React, { useState, useEffect } from "react";
import { Formik, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import { closeModal, openModal } from "react-url-modal";
import { format } from "date-fns";
import { Modal, Button, TextareaInput, ToggleSwitch } from "@/components";
import { useAppDispatch, useAppSelector } from "@/redux/store";
import { fetchCreateGame, fetchGetCreatedGames, fetchGetGamesList, setGames } from "@/redux/games";
import { getProfileInfo } from "@/redux/profile/settings/getProfileInfo/selectors";
import { getTokenFromStorage } from "@/utils";
import { openAuthModal } from "@/utils/authModal/openAuthModal";
import { phoneMaskFormat } from "@/utils/form/phoneMask";
import { ICreateGameData } from "@/redux/games/types";
import { DateNavigation } from "./components/DateNavigation";
import { WeekDays } from "./components/WeekDays";
import { TimeTable } from "./components/TimeTable";
import { CheckboxGroup } from "./components/CheckboxGroup";
import { QuantityEmptySeats } from "./components/QuantityEmptySeats";
import styles from "./CreateNewGameModal.module.scss";
import { RulesAndPolicyText } from "@/components/RulesAndPolicyText"

interface CreateNewGameModalProps {
  setIsOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  params?: Record<string, any>;
}

const CreateNewGameModalComponent = ({
  setIsOpen,
  params = {},
}: CreateNewGameModalProps): JSX.Element => {
  const dispatch = useAppDispatch();
  const profileInfo = useAppSelector(getProfileInfo);

  const [selectedDay, setSelectedDay] = useState<string>(format(new Date(), "yyyy-MM-dd"));
  const [selectedTimes, setSelectedTimes] = useState<string[]>([]);
  const [selectedLevels, setSelectedLevels] = useState<string[]>([]);
  const [quantity, setQuantity] = useState<number>(1);
  const [isRented, setIsRented] = useState<boolean>(false);

  // Проверка авторизации при открытии модалки
  useEffect(() => {
    const token = getTokenFromStorage();
    if (!token) {
      handleClose();
      openAuthModal(dispatch);
    }
  }, [dispatch]);

  const organizerName = profileInfo.profileInfo?.name || "";
  const organizerPhone = profileInfo.profileInfo?.phone || "";

  const initialValues = {
    comment: "",
  };

  const validationSchema = Yup.object({
    comment: Yup.string().max(500, "Комментарий должен быть до 500 символов"),
  });

  const handleClose = () => {
    closeModal();
    if (setIsOpen) {
      setIsOpen(false);
    }
  };

  const handleSubmit = async (values: typeof initialValues, { setSubmitting }: any) => {
    if (selectedTimes.length === 0) {
      // TODO: Показать ошибку
      setSubmitting(false);
      return;
    }

    try {
      // Сортируем времена и берем первое как начало
      const sortedTimes = [...selectedTimes].sort();
      const startTime = sortedTimes[0];
      const startHour = parseInt(startTime.split(":")[0]);
      // Если выбрано два времени, используем второе как конец, иначе добавляем 1 час
      const endHour = sortedTimes.length > 1 
        ? parseInt(sortedTimes[sortedTimes.length - 1].split(":")[0]) + 1
        : startHour + 1;
      const endTime = `${endHour.toString().padStart(2, "0")}:${startTime.split(":")[1]}`;

      const data: ICreateGameData = {
        date: selectedDay,
        startTime: startTime,
        endTime: endTime,
        level: selectedLevels,
        emptySlots: quantity,
        name: organizerName,
        phone: `+${organizerPhone.replace(/[^0-9]/g, "")}`,
        comment: values.comment.trim() || undefined,
        isRented: isRented,
      };

      await dispatch(fetchCreateGame(data)).unwrap();

      // Обновляем списки игр
      await Promise.all([
        dispatch(fetchGetCreatedGames()),
        dispatch(fetchGetGamesList()),
      ]);

      // Закрываем модальное окно создания и открываем окно успеха
      handleClose();
      openModal({ name: "successCreateGame" });
    } catch (error) {
      console.error("Error creating game:", error);
      // TODO: Показать ошибку пользователю
    } finally {
      setSubmitting(false);
    }
  };

  const isDisabled =
    !selectedDay ||
    selectedTimes.length === 0 ||
    selectedLevels.length === 0 ||
    quantity === 0;

  return (
    <Modal
      title="Создать игру"
      isOpen={true}
      setIsOpen={setIsOpen || (() => {})}
      closeModal={handleClose}
      modalWindowWidth={600}
    >
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
        validateOnChange={true}
        validateOnBlur={true}
      >
        {(formik) => {
          const isFormDisabled = isDisabled || formik.isSubmitting;

          return (
            <Form className={styles.form}>
              <div className={styles.section}>
                <h3 className={styles.sectionTitle}>Дата и время (Выберите интервал)</h3>
                <DateNavigation
                  date={selectedDay}
                  onDateChange={(newDate) => {
                    console.log("[CreateNewGameModal] DateNavigation changed:", newDate);
                    setSelectedDay(newDate);
                    setSelectedTimes([]);
                  }}
                />
                <WeekDays
                  date={selectedDay}
                  selectedDate={selectedDay}
                  onDayClick={(dateString) => {
                    console.log("[CreateNewGameModal] WeekDays day clicked:", dateString, "current selectedDay:", selectedDay);
                    setSelectedDay(dateString);
                    setSelectedTimes([]);
                  }}
                />
                <div className={styles.timeContainer}>
                  <TimeTable selectedDay={selectedDay} onTimeChange={setSelectedTimes} />
                </div>
              </div>

              <div className={styles.section}>
                <div className={styles.sectionTitle}>Уровень</div>
                <CheckboxGroup
                  selectedValues={selectedLevels}
                  onSelectionChange={setSelectedLevels}
                />
              </div>

              <QuantityEmptySeats quantity={quantity} setQuantity={setQuantity} />

              <div className={styles.organizerRow}>
                <div className={styles.organizerField}>
                  <div className={styles.organizerLabel}>Имя организатора</div>
                  <div className={styles.organizerValue}>{organizerName}</div>
                </div>
                <div className={styles.organizerField}>
                  <div className={styles.organizerLabel}>Номер для связи с Вами</div>
                  <div className={styles.organizerValue}>{phoneMaskFormat(organizerPhone)}</div>
                </div>
              </div>

              <div className={styles.section}>
                <div className={styles.sectionTitle}>Комментарий к игре</div>
                <div className={styles.inputSection}>
                  <div className={styles.textareaWrapper}>
                    <TextareaInput
                      name="comment"
                      placeholder="Комментарий"
                      className={styles.textarea}
                    />
                    <div className={styles.commentInfo}>
                      <span className={formik.values.comment.length === 500 ? styles.commentWarning : ""}>
                        {formik.values.comment.length}/500
                      </span>
                    </div>
                  </div>
                  {formik.values.comment.length === 500 && (
                    <div className={styles.commentWarningText}>
                      Текст должен быть до 500 символов
                    </div>
                  )}
                  <ErrorMessage component="div" className={styles.errorMessage} name="comment" />
                </div>

                <div className={styles.switchRow}>
                  <span className={styles.switchLabel}>Корт забронирован</span>
                  <ToggleSwitch
                    checked={isRented}
                    onChange={setIsRented}
                    id="isRented"
                  />
                </div>
              </div>

              <RulesAndPolicyText />

              <Button
                type="submit"
                disabled={isFormDisabled}
                newStyles={styles.submitButton}
              >
                {formik.isSubmitting ? "Создание..." : "Создать игру"}
              </Button>
            </Form>
          );
        }}
      </Formik>
    </Modal>
  );
};

export default CreateNewGameModalComponent;
export const CreateNewGameModal = CreateNewGameModalComponent;
